/**
 * Chat Orchestrator
 * Routes messages through the Cloudflare Worker API — no client-side API keys.
 */

import { store, addMessage, updateMessage, setStreaming, createConversation, addAgentMessage, updateAgentMessage, setAgentHistory, prependAgentHistory, clearAttachedFiles, setFollowUps, setRoutingSuggestion, setMemorySuggestion, setUsageData } from './appState.js';
import { providers } from '../providers/registry.js';
import { streamChat, fetchTranscript, fetchAgentHistory, sendAgentMessage as apiSendAgentMessage } from './api.js';
import { db } from './db.js';
import { getRAGContext } from './documents.js';
import { buildMemoryContext, detectMemorySuggestion } from './memory.js';
import { shouldSearch, performSearch, formatSearchContext } from './search.js';
import { trackUsage, getConversationUsage, getUsage } from './usage.js';
import { suggestModel } from './routing.js';

// Active abort controllers for canceling streams
const activeStreams = new Map();

/**
 * Send a message and stream the response
 * @param {string} userInput - The user's message text
 * @param {string} [providerId] - Specific provider to use
 */
export async function sendMessage(userInput, providerId) {
  if (!userInput.trim()) return;

  const state = store.getState();
  let conversationId = state.chat.currentConversationId;

  // Create conversation if none exists
  if (!conversationId) {
    createConversation();
    conversationId = store.getState().chat.currentConversationId;
  }

  // Clear routing suggestion and follow-ups on new message
  setRoutingSuggestion(null);
  setFollowUps([]);

  // Detect memory suggestion from user message
  const memSuggestion = detectMemorySuggestion(userInput);
  if (memSuggestion.shouldSuggest) {
    setMemorySuggestion({ content: memSuggestion.suggestion });
  }

  // Add user message
  addMessage({
    conversationId,
    message: { role: 'user', content: userInput, status: 'complete' },
  });

  // Persist user message
  const updatedState = store.getState();
  const conv = updatedState.conversations.find(c => c.id === conversationId);
  await db.saveConversation(conv);

  // Resolve provider — check if smart routing override
  const active = providers.getActive();
  const resolvedProvider = providerId ? providers.get(providerId) : (active ? providers.get(active.providerId) : null);

  if (!resolvedProvider || !resolvedProvider.configured) {
    addMessage({
      conversationId,
      message: {
        role: 'assistant',
        content: 'No AI provider available. The server may be starting up — try again in a moment.',
        model: 'System',
        status: 'error',
      },
    });
    return;
  }

  // Determine model to use (may have been overridden by routing)
  const modelToUse = providerId ? resolvedProvider.model : (active?.model || resolvedProvider.model);

  // Add placeholder assistant message
  addMessage({
    conversationId,
    message: {
      role: 'assistant',
      content: '',
      model: resolvedProvider.name,
      status: 'streaming',
    },
  });

  const stateAfterAdd = store.getState();
  const convAfterAdd = stateAfterAdd.conversations.find(c => c.id === conversationId);
  const assistantMsg = convAfterAdd.messages[convAfterAdd.messages.length - 1];

  setStreaming(true);

  const abortController = new AbortController();
  activeStreams.set(conversationId, abortController);

  try {
    // Build message history
    const history = convAfterAdd.messages
      .filter(m => m.status !== 'error' && m.id !== assistantMsg.id)
      .map(m => ({ role: m.role, content: m.content }));

    // Gather RAG context from attached files + active KB docs
    const attachedDocIds = stateAfterAdd.attachedFiles.map(f => f.docId);
    let ragContext = null;
    try {
      const ragResults = await getRAGContext(userInput, {
        conversationId,
        attachedDocIds,
      });
      if (ragResults.length > 0) ragContext = ragResults;
    } catch { /* RAG is best-effort */ }

    // Web search (if enabled)
    let searchContext = null;
    if (stateAfterAdd.settings.searchEnabled || shouldSearch(userInput)) {
      if (stateAfterAdd.settings.searchEnabled) {
        try {
          const results = await performSearch(userInput);
          if (results.length > 0) {
            searchContext = { results, formatted: formatSearchContext(results) };
          }
        } catch { /* search is best-effort */ }
      }
    }

    // Build memory context
    const memoryContext = buildMemoryContext();

    // Clear attached files after use
    clearAttachedFiles();

    let fullContent = '';
    const settings = stateAfterAdd.settings;

    for await (const chunk of streamChat({
      provider: resolvedProvider.id,
      model: modelToUse,
      messages: history,
      temperature: settings.temperature,
      maxTokens: settings.maxTokens,
      signal: abortController.signal,
      ragContext,
      memoryContext: memoryContext || undefined,
      searchContext: searchContext?.formatted || undefined,
    })) {
      if (abortController.signal.aborted) break;

      if (chunk.type === 'text') {
        fullContent += chunk.content;
        updateMessage({
          conversationId,
          messageId: assistantMsg.id,
          updates: { content: fullContent, status: 'streaming' },
        });
      } else if (chunk.type === 'error') {
        updateMessage({
          conversationId,
          messageId: assistantMsg.id,
          updates: { content: `Error: ${chunk.content}`, status: 'error' },
        });
        break;
      } else if (chunk.type === 'done') {
        break;
      }
    }

    // Mark complete
    if (!abortController.signal.aborted) {
      const messageUpdates = {
        content: fullContent,
        status: 'complete',
        ragSources: ragContext || undefined,
      };
      if (searchContext?.results) {
        messageUpdates.searchResults = searchContext.results;
      }
      updateMessage({
        conversationId,
        messageId: assistantMsg.id,
        updates: messageUpdates,
      });

      // Track usage
      try {
        await trackUsage(conversationId, modelToUse, userInput, fullContent);
        const convUsage = getConversationUsage(conversationId);
        const totalUsage = getUsage();
        setUsageData({
          currentConv: convUsage,
          total: totalUsage.total,
        });
      } catch { /* usage tracking is best-effort */ }

      // Generate follow-up suggestions
      if (settings.followUpSuggestions && fullContent.length > 50) {
        const followUps = generateFollowUps(userInput, fullContent);
        if (followUps.length > 0) {
          setFollowUps(followUps);
        }
      }
    }

    // Persist
    const finalState = store.getState();
    const finalConv = finalState.conversations.find(c => c.id === conversationId);
    await db.saveConversation(finalConv);

  } catch (error) {
    updateMessage({
      conversationId,
      messageId: assistantMsg.id,
      updates: { content: `Error: ${error.message}`, status: 'error' },
    });
  } finally {
    setStreaming(false);
    activeStreams.delete(conversationId);
  }
}

/**
 * Send to multiple providers in parallel
 */
export async function sendParallel(userInput, modelIds) {
  if (!userInput.trim()) return;

  const state = store.getState();
  let conversationId = state.chat.currentConversationId;

  if (!conversationId) {
    createConversation();
    conversationId = store.getState().chat.currentConversationId;
  }

  // Add user message once
  addMessage({
    conversationId,
    message: { role: 'user', content: userInput, status: 'complete' },
  });

  const updatedState = store.getState();
  const conv = updatedState.conversations.find(c => c.id === conversationId);
  await db.saveConversation(conv);

  setStreaming(true);

  const promises = modelIds.map(async (modelId) => {
    const provider = providers.get(modelId);
    if (!provider?.configured) {
      addMessage({
        conversationId,
        message: {
          role: 'assistant',
          content: `${modelId} not available.`,
          model: modelId,
          status: 'error',
        },
      });
      return;
    }

    // Add placeholder
    addMessage({
      conversationId,
      message: {
        role: 'assistant',
        content: '',
        model: provider.name,
        status: 'streaming',
      },
    });

    const stateNow = store.getState();
    const convNow = stateNow.conversations.find(c => c.id === conversationId);
    const aMsg = convNow.messages[convNow.messages.length - 1];

    const history = convNow.messages
      .filter(m => m.status !== 'error' && m.role !== 'assistant')
      .map(m => ({ role: m.role, content: m.content }));

    let fullContent = '';
    try {
      for await (const chunk of streamChat({
        provider: provider.id,
        model: provider.model,
        messages: history,
        temperature: stateNow.settings.temperature,
        maxTokens: stateNow.settings.maxTokens,
      })) {
        if (chunk.type === 'text') {
          fullContent += chunk.content;
          updateMessage({
            conversationId,
            messageId: aMsg.id,
            updates: { content: fullContent, status: 'streaming' },
          });
        } else if (chunk.type === 'error') {
          updateMessage({
            conversationId,
            messageId: aMsg.id,
            updates: { content: chunk.content, status: 'error' },
          });
          return;
        } else if (chunk.type === 'done') {
          break;
        }
      }

      updateMessage({
        conversationId,
        messageId: aMsg.id,
        updates: { content: fullContent, status: 'complete' },
      });
    } catch (error) {
      updateMessage({
        conversationId,
        messageId: aMsg.id,
        updates: { content: `Error: ${error.message}`, status: 'error' },
      });
    }
  });

  await Promise.allSettled(promises);
  setStreaming(false);

  const finalState = store.getState();
  const finalConv = finalState.conversations.find(c => c.id === conversationId);
  await db.saveConversation(finalConv);
}

/**
 * Stop the current stream for a conversation
 */
export function stopStream(conversationId) {
  const controller = activeStreams.get(conversationId);
  if (controller) {
    controller.abort();
    activeStreams.delete(conversationId);
    setStreaming(false);
  }
}

/**
 * Load conversations from IndexedDB into state on startup
 */
export async function loadStoredConversations() {
  try {
    const conversations = await db.loadConversations();
    if (conversations.length > 0) {
      store.setState({
        conversations,
        chat: { currentConversationId: conversations[0].id },
      });
    }
  } catch (error) {
    console.error('Failed to load conversations:', error);
  }
}

/**
 * Extract human-readable text from a transcript message
 * Handles Telegram format, system events, and array content
 */
function extractReadableContent(message) {
  let content = message.content;
  
  // Extract text from array content
  if (Array.isArray(content)) {
    content = content
      .filter(c => c.type === 'text')
      .map(c => c.text)
      .join('\n');
  }
  
  if (typeof content !== 'string') return '';
  
  // For user messages: extract human text from Telegram format
  if (message.role === 'user') {
    // Skip pure system messages (exec outputs, heartbeats, compaction flushes)
    // BUT don't skip if there's a Telegram message bundled in
    if (/^System:\s*\[/i.test(content.trim()) && !content.includes('[Telegram ')) return '';
    if (/^Pre-compaction memory flush/i.test(content.trim())) return '';
    if (/^Read HEARTBEAT\.md/i.test(content.trim())) return '';
    
    // Extract from Telegram format: [Telegram ETS id:... UTC] actual message
    const telegramMatch = content.match(/\[Telegram\s+\w+\s+id:\d+[^\]]*\]\s*([\s\S]*?)(?:\n\[message_id:|\s*$)/);
    if (telegramMatch) {
      return telegramMatch[1].trim();
    }
    
    // Extract from sessions_send format: [Wed 2026-02-11 23:07 UTC] actual message
    const sessionsSendMatch = content.match(/^\[(Mon|Tue|Wed|Thu|Fri|Sat|Sun)\s+\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}\s+UTC\]\s*([\s\S]*?)$/);
    if (sessionsSendMatch) {
      return sessionsSendMatch[2].trim();
    }
    
    // If it starts with system events but has telegram messages later
    const lines = content.split('\n');
    const humanLines = [];
    let foundHuman = false;
    for (const line of lines) {
      if (line.match(/^\[Telegram\s+\w+\s+id:\d+/)) {
        const m = line.match(/\]\s*(.*)/);
        if (m && m[1].trim()) {
          humanLines.push(m[1].trim());
          foundHuman = true;
        }
      } else if (foundHuman || (!line.match(/^System:\s*\[/) && !line.match(/^\[message_id:/) && !line.match(/^\[2026-/) && line.trim())) {
        // Keep non-system lines
        if (!line.match(/^System:\s*\[/) && !line.match(/^\[message_id:/)) {
          humanLines.push(line);
        }
      }
    }
    
    const result = humanLines.join('\n').trim();
    // If everything was system events, skip
    if (!result || result.match(/^(System:|Exec completed|Exec failed)/)) return '';
    return result;
  }
  
  // For assistant messages: strip thinking/reasoning blocks
  content = content.replace(/<antThinking>[\s\S]*?<\/antThinking>/g, '').trim();
  
  // Filter out NO_REPLY, HEARTBEAT_OK (with or without timestamp prefix)
  const stripped = content.replace(/^\[.*?\]\s*/s, '').trim();
  if (!stripped || stripped === 'NO_REPLY' || stripped === 'HEARTBEAT_OK' || stripped === 'HEARTBEAT_OK\nNO_REPLY') return '';
  // Also catch multiline NO_REPLY patterns
  if (/^\s*NO_REPLY\s*$/m.test(content) && content.replace(/NO_REPLY/g, '').replace(/\[.*?\]/g, '').trim() === '') return '';
  
  return content;
}

/**
 * Load agent transcript history into the chat view
 * Called when switching to an agent tab
 * @param {string} tabId - Agent tab ID (e.g., 'vps:main', 'local:reviewer-local')
 */
export async function loadAgentHistory(tabId) {
  const state = store.getState();
  const agentConv = state.agentConversations[tabId];
  
  // Skip if history already loaded, unless we want to force refresh
  // if (agentConv?.historyLoaded) return;

  try {
    const data = await fetchAgentHistory(tabId, 500, 0);
    if (data.messages && data.messages.length > 0) {
      // Convert transcript messages to chat format
      // Filter: only user + assistant, skip toolResult/toolCall
      const messages = data.messages
        .filter(m => m.role === 'user' || m.role === 'assistant')
        .map(m => {
          const content = extractReadableContent(m);
          return {
            id: crypto.randomUUID(),
            role: m.role,
            content: content || '',
            status: 'complete',
            timestamp: m.timestamp || Date.now(),
            fromHistory: true,
          };
        })
        .filter(m => m.content.trim()); // Skip empty messages

      setAgentHistory({
        agentId: tabId,
        messages,
        hasMore: data.hasMore || false,
        total: data.total || messages.length,
        offset: data.offset || messages.length,
      });
      // Set baseline for polling
      _agentPollLastCount = data.total || messages.length;
    } else {
      // Mark as loaded even if no messages
      setAgentHistory({ agentId: tabId, messages: [], hasMore: false, total: 0, offset: 0 });
    }
  } catch (error) {
    console.error(`Failed to load agent history for ${tabId}:`, error);
  }
}

/**
 * Load more (older) agent history
 * @param {string} tabId - Agent tab ID
 */
export async function loadMoreAgentHistory(tabId) {
  const state = store.getState();
  const agentConv = state.agentConversations[tabId];
  if (!agentConv || !agentConv.hasMore) return;

  try {
    const data = await fetchAgentHistory(tabId, 500, agentConv.offset || 0);
    if (data.messages && data.messages.length > 0) {
      const messages = data.messages
        .filter(m => m.role === 'user' || m.role === 'assistant')
        .map(m => {
          const content = extractReadableContent(m);
          return {
            id: crypto.randomUUID(),
            role: m.role,
            content: content || '',
            status: 'complete',
            timestamp: m.timestamp || Date.now(),
            fromHistory: true,
          };
        })
        .filter(m => m.content.trim());

      prependAgentHistory({
        agentId: tabId,
        messages,
        hasMore: data.hasMore || false,
        offset: data.offset || (agentConv.offset + messages.length),
      });
    }
  } catch (error) {
    console.error(`Failed to load more agent history for ${tabId}:`, error);
  }
}

// ─── Agent Chat Polling ──────────────────────
let _agentPollTimer = null;
let _agentPollTabId = null;
let _agentPollLastCount = 0;
let _agentPollNoChangeCount = 0;
const POLL_INTERVAL_FAST = 5000;   // 5s when active
const POLL_INTERVAL_SLOW = 30000;  // 30s after no changes
const POLL_SLOWDOWN_AFTER = 60;    // slow down after 60 polls with no change (5 min)

/**
 * Start polling for new messages on an agent tab
 */
export function startAgentPolling(tabId) {
  // Stop any existing polling
  stopAgentPolling();
  
  _agentPollTabId = tabId;
  _agentPollNoChangeCount = 0;
  
  const poll = async () => {
    // Don't poll if tab changed or page hidden
    if (_agentPollTabId !== tabId) return;
    if (document.hidden) {
      _agentPollTimer = setTimeout(poll, POLL_INTERVAL_FAST);
      return;
    }
    
    try {
      const data = await fetchAgentHistory(tabId, 500, 0);
      const newTotal = data.total || 0;
      console.log(`[AgentPoll] total=${newTotal}, lastCount=${_agentPollLastCount}, changed=${newTotal !== _agentPollLastCount}`);
      
      if (data.messages && data.messages.length > 0 && newTotal !== _agentPollLastCount) {
        // Message count changed — update the view
        const messages = data.messages
          .filter(m => m.role === 'user' || m.role === 'assistant')
          .map(m => {
            const content = extractReadableContent(m);
            return {
              id: crypto.randomUUID(),
              role: m.role,
              content: content || '',
              status: 'complete',
              timestamp: m.timestamp || Date.now(),
              fromHistory: true,
            };
          })
          .filter(m => m.content.trim());
        
        setAgentHistory({
          agentId: tabId,
          messages,
          hasMore: data.hasMore || false,
          total: data.total || messages.length,
          offset: data.offset || messages.length,
        });
        // Auto-scroll to bottom when new messages arrive
        setTimeout(() => {
          const chatContainer = document.querySelector('.chat-container');
          if (chatContainer) chatContainer.scrollTop = chatContainer.scrollHeight;
        }, 100);
        _agentPollNoChangeCount = 0;
      } else {
        _agentPollNoChangeCount++;
      }
      
      _agentPollLastCount = newTotal;
    } catch (err) {
      // Silent fail — don't spam console during polling
    }
    
    // Schedule next poll (slow down if no changes)
    const interval = _agentPollNoChangeCount >= POLL_SLOWDOWN_AFTER 
      ? POLL_INTERVAL_SLOW 
      : POLL_INTERVAL_FAST;
    _agentPollTimer = setTimeout(poll, interval);
  };
  
  // Start first poll after initial delay (history already loaded)
  _agentPollTimer = setTimeout(poll, POLL_INTERVAL_FAST);
}

/**
 * Stop agent chat polling
 */
export function stopAgentPolling() {
  if (_agentPollTimer) {
    clearTimeout(_agentPollTimer);
    _agentPollTimer = null;
  }
  _agentPollTabId = null;
  _agentPollLastCount = 0;
  _agentPollNoChangeCount = 0;
}

// Pause/resume on visibility change
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden && _agentPollTabId) {
      // Resume with fast interval when page becomes visible again
      _agentPollNoChangeCount = 0;
    }
  });
}

/**
 * Send a message to an OpenClaw agent tab
 * @param {string} agentId - Agent identifier (main, pulse, sentinel, etc.)
 * @param {string} userInput - User message
 */
export async function sendAgentMessage(agentId, userInput) {
  if (!userInput.trim()) return;

  // Add user message to agent conversation
  addAgentMessage({
    agentId,
    message: { role: 'user', content: userInput, status: 'complete' },
  });

  // Add placeholder assistant message
  addAgentMessage({
    agentId,
    message: {
      role: 'assistant',
      content: '',
      model: agentId,
      status: 'streaming',
    },
  });

  const state = store.getState();
  const agentConv = state.agentConversations[agentId];
  const assistantMsg = agentConv.messages[agentConv.messages.length - 1];

  setStreaming(true);

  try {
    // Parse tabId: "vps:main" → env=vps, sessionKey=main
    const colonIdx = agentId.indexOf(':');
    const env = colonIdx > 0 ? agentId.substring(0, colonIdx) : 'vps';
    const sessionKey = colonIdx > 0 ? agentId.substring(colonIdx + 1) : agentId;

    // Try sessions_send first (persistent session, survives refresh)
    let usedSessionsSend = false;
    try {
      const result = await apiSendAgentMessage(sessionKey, userInput, env);
      
      if (result.ok && result.response) {
        usedSessionsSend = true;
        let responseText = '';
        if (typeof result.response === 'string') {
          responseText = result.response;
        } else if (result.response.message) {
          responseText = result.response.message;
        } else if (result.response.content) {
          responseText = result.response.content;
        } else if (result.response.result) {
          responseText = typeof result.response.result === 'string' 
            ? result.response.result 
            : JSON.stringify(result.response.result);
        }
        
        updateAgentMessage({
          agentId,
          messageId: assistantMsg.id,
          updates: { content: responseText || '...', status: responseText ? 'complete' : 'streaming' },
        });
        
        if (!responseText) {
          await waitForSessionResponse(agentId, assistantMsg.id);
        }
      } else if (result.error && result.error.includes('No session found')) {
        // Agent doesn't have a persistent session — fall back to streaming
        usedSessionsSend = false;
      } else {
        usedSessionsSend = true;
        // Unknown response — try polling
        updateAgentMessage({
          agentId,
          messageId: assistantMsg.id,
          updates: { content: '...', status: 'streaming' },
        });
        await waitForSessionResponse(agentId, assistantMsg.id);
      }
    } catch (e) {
      // sessions_send failed — fall back to streaming
      console.warn('sessions_send failed, falling back to streaming:', e.message);
      usedSessionsSend = false;
    }

    // Fallback: streaming chat (stateless, won't persist after refresh)
    if (!usedSessionsSend) {
      const history = agentConv.messages
        .filter(m => m.status !== 'error' && m.id !== assistantMsg.id)
        .map(m => ({ role: m.role, content: m.content }));

      let fullContent = '';
      const settings = state.settings;

      for await (const chunk of streamChat({
        provider: 'openclaw',
        model: agentId,
        messages: history,
        temperature: settings.temperature,
        maxTokens: settings.maxTokens,
      })) {
        if (chunk.type === 'text') {
          fullContent += chunk.content;
          updateAgentMessage({
            agentId,
            messageId: assistantMsg.id,
            updates: { content: fullContent, status: 'streaming' },
          });
        } else if (chunk.type === 'error') {
          updateAgentMessage({
            agentId,
            messageId: assistantMsg.id,
            updates: { content: `Error: ${chunk.content}`, status: 'error' },
          });
          break;
        } else if (chunk.type === 'done') {
          break;
        }
      }

      updateAgentMessage({
        agentId,
        messageId: assistantMsg.id,
        updates: { content: fullContent, status: 'complete' },
      });
    }
  } catch (error) {
    updateAgentMessage({
      agentId,
      messageId: assistantMsg.id,
      updates: { content: `Error: ${error.message}`, status: 'error' },
    });
  } finally {
    setStreaming(false);
    generateFollowUps(agentId);
  }
}

// Wait for the agent's response via polling the session history
async function waitForSessionResponse(agentId, placeholderId) {
  const maxWait = 120000;
  const start = Date.now();
  
  while (Date.now() - start < maxWait) {
    await new Promise(r => setTimeout(r, 2000));
    
    try {
      const data = await fetchAgentHistory(agentId, 5, 0);
      if (data.messages && data.messages.length > 0) {
        // Find the latest assistant message
        const latest = data.messages
          .filter(m => m.role === 'assistant')
          .pop();
        
        if (latest) {
          const content = extractReadableContent(latest);
          if (content && content.trim() && content !== '...') {
            updateAgentMessage({
              agentId,
              messageId: placeholderId,
              updates: { content, status: 'complete' },
            });
            return;
          }
        }
      }
    } catch (e) {
      console.error('Poll error:', e);
    }
  }
  
  updateAgentMessage({
    agentId,
    messageId: placeholderId,
    updates: { content: 'Response timeout — check agent session directly.', status: 'error' },
  });
}

/**
 * Generate follow-up question suggestions from the AI response
 * @param {string} userQuery - The user's original query
 * @param {string} response - The AI's response
 * @returns {Array<{text: string}>}
 */
function generateFollowUps(userQuery, response) {
  const followUps = [];

  // Extract key topics from the response (simple heuristic)
  const sentences = response.split(/[.!?]+/).filter(s => s.trim().length > 20);
  if (sentences.length < 2) return [];

  // Strategy 1: Ask to elaborate on a topic mentioned in the response
  const topics = extractTopics(response);
  if (topics.length > 0) {
    followUps.push({ text: `Tell me more about ${topics[0]}` });
  }

  // Strategy 2: Ask for examples
  if (/\b(concept|approach|method|technique|pattern|principle)\b/i.test(response)) {
    followUps.push({ text: 'Can you give me a practical example?' });
  } else if (/\b(code|function|class|implementation)\b/i.test(response)) {
    followUps.push({ text: 'Can you show the complete implementation?' });
  }

  // Strategy 3: Ask to compare or contrast
  if (topics.length >= 2) {
    followUps.push({ text: `How does ${topics[0]} compare to ${topics[1]}?` });
  }

  // Strategy 4: Ask for pros/cons or trade-offs
  if (followUps.length < 3 && !followUps.some(f => f.text.includes('pros'))) {
    followUps.push({ text: 'What are the potential downsides?' });
  }

  return followUps.slice(0, 3);
}

/**
 * Extract key topics/noun phrases from text
 * @param {string} text
 * @returns {string[]}
 */
function extractTopics(text) {
  // Look for capitalized phrases, quoted terms, bold terms
  const topics = new Set();

  // Bold/emphasized terms (from markdown)
  const boldMatches = text.match(/\*\*([^*]+)\*\*/g);
  if (boldMatches) {
    for (const m of boldMatches.slice(0, 5)) {
      const term = m.replace(/\*\*/g, '').trim();
      if (term.length > 2 && term.length < 50) topics.add(term.toLowerCase());
    }
  }

  // Heading-like patterns
  const headingMatches = text.match(/^#{1,3}\s+(.+)$/gm);
  if (headingMatches) {
    for (const m of headingMatches.slice(0, 3)) {
      const term = m.replace(/^#+\s+/, '').trim();
      if (term.length > 2 && term.length < 50) topics.add(term.toLowerCase());
    }
  }

  return [...topics].slice(0, 4);
}

/**
 * Update routing suggestion based on current input
 * @param {string} query - Current input text
 */
export function updateRoutingSuggestionForInput(query) {
  const state = store.getState();
  if (!state.settings.smartRouting || state.parallelMode.enabled) {
    setRoutingSuggestion(null);
    return;
  }

  const suggestion = suggestModel(query, (providerId) => {
    const p = providers.get(providerId);
    return p?.configured;
  });

  // Don't suggest the currently active provider+model
  if (suggestion) {
    const active = providers.getActive();
    if (active && active.providerId === suggestion.provider && active.model === suggestion.model) {
      setRoutingSuggestion(null);
      return;
    }
  }

  setRoutingSuggestion(suggestion);
}
